import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoBack } from '../hooks/useGoBack'
import { loadStripe } from '@stripe/stripe-js'
import Header from '../components/Header'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { demoUiEnabled } from '../lib/appEnv'

/**
 * #1189 Phase 3: Stripe LPM (Alipay / WeChat Pay 等) の redirect 復帰 landing。
 *
 * Stripe が `?payment_intent=...&payment_intent_client_secret=...&redirect_status=...`
 * を URL に付加して return_url に飛ばしてくる。ここで:
 *   1. Stripe.js の retrievePaymentIntent で最新の status を確認
 *   2. `succeeded` or `processing` → charge_id の intent を webhook 反映まで poll
 *   3. `executed` を検知したら /charge/success へ (ChargeBank と同じ pattern)
 *   4. `failed` / `canceled` → error 表示 + /charge に戻す動線
 *
 * ChargeBank との違い: bank_transfer_jp は自分の画面から離れずに polling する
 * のに対し、LPM は redirect で 一度画面を離れてから戻ってくる。intent の
 * charge_id は Stripe から URL に付いてこないため、confirmPayment の
 * return_url に `?charge_id=xxx` を自前で足しておく設計 (ChargeLocal 側)。
 *
 * #2190 項目 5「失敗」と出すのは、**失敗したと確かに言えるとき**だけ (Stripe の状態が
 * requires_payment_method = 拒否・中断、または canceled)。それ以外 — Stripe.js の読み込みや状態の取得に
 * 失敗した・3D セキュアの途中・処理中・成立 — は分からないとして、サーバに残高に入ったかを聞き続ける
 * (最長 POLL_LIMIT_MS)。以前は分からないときも「支払を完了できませんでした」+「チャージ画面に戻る」を出し、
 * 成立していたかもしれないチャージに 2 回目を誘っていた。時間切れでも払い直させず、ウォレットへ案内する
 * (ウォレットを開くとサーバが未反映のチャージを確かめ直す)。
 */

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
)

/** 残高への反映を待つ長さ (通常 30 秒以内。webhook が遅れても、画面からの確認でサーバが確かめに行く) */
const POLL_LIMIT_MS = 120_000

/** Stripe の状態のうち、失敗したと確かに言えるもの (お金は動いていない) */
const FAILED_STATUSES = new Set(['requires_payment_method', 'canceled'])

type Phase = 'checking' | 'polling' | 'unconfirmed' | 'failed'

export default function ChargeReturn() {
  const navigate = useNavigate()
  // #1361 戻るは履歴を1つ戻す（行き先を push すると履歴が積み上がる）
  const goBack = useGoBack('/charge')
  const [searchParams] = useSearchParams()
  const { t } = useI18n()

  const chargeId = searchParams.get('charge_id')
  const clientSecret = searchParams.get('payment_intent_client_secret')

  const [phase, setPhase] = useState<Phase>('checking')
  const [error, setError] = useState<string | null>(null)
  // StrictMode double-fire で retrievePaymentIntent を 2 回叩かないための guard。
  const checkedRef = useRef(false)

  useEffect(() => {
    if (checkedRef.current) return
    checkedRef.current = true
    ;(async () => {
      // 分からないときは、サーバに残高に入ったかを聞き続ける (charge_id が無ければ、聞けないので案内だけ)
      const keepAsking = () => setPhase(chargeId ? 'polling' : 'unconfirmed')
      if (!clientSecret) {
        if (chargeId) { keepAsking(); return }
        setPhase('failed')
        setError(t('chargeReturn.errorMissingParams'))
        return
      }
      // 1. Stripe.js に PI 現状 status を問合せ。読み込み・取得の失敗は「分からない」(失敗ではない)
      let paymentIntent: { status: string } | undefined
      try {
        const stripe = await stripePromise
        if (!stripe) { keepAsking(); return }
        const res = await stripe.retrievePaymentIntent(clientSecret)
        if (res.error || !res.paymentIntent) { keepAsking(); return }
        paymentIntent = res.paymentIntent
      } catch (e) {
        console.error('[ChargeReturn] could not read the payment:', e)
        keepAsking()
        return
      }
      // Stripe の PI status:
      //   requires_payment_method — 支払失敗 / 中断 (お金は動いていない)
      //   canceled     — 明示的にキャンセル
      //   それ以外 (succeeded / processing / requires_action = 3D セキュアの途中 等) は、成立しうる
      if (FAILED_STATUSES.has(paymentIntent.status)) {
        setPhase('failed')
        setError(t('chargeReturn.errorPaymentFailed'))
        return
      }
      // 2. 残高に反映されるまで /charge/intent/{id} を poll
      keepAsking()
    })()
  }, [clientSecret, chargeId, t])

  // polling: charge_id の intent status が executed になったら /charge/success へ
  useEffect(() => {
    if (phase !== 'polling' || !chargeId) return
    let cancelled = false
    const startedAt = Date.now()
    const poll = async () => {
      if (cancelled) return
      if (Date.now() - startedAt > POLL_LIMIT_MS) {
        // 時間切れ。払い直させず、ウォレットで確かめてもらう
        cancelled = true
        setPhase('unconfirmed')
        return
      }
      try {
        const status = await api.getChargeIntentStatus(chargeId)
        if (cancelled) return
        if (status?.status === 'expired' || status?.status === 'canceled') {
          // サーバがこのチャージを閉じた (残高には入らない)
          cancelled = true
          setPhase('failed')
          setError(t('chargeReturn.errorPaymentFailed'))
          return
        }
        if (status?.status === 'executed') {
          const { userId, restoreSession } = useStore.getState()
          if (userId) {
            try { await restoreSession() } catch { /* best-effort */ }
          }
          useStore.setState({
            lastChargeAmount: status.amount_jpy,
            lastChargeTxHash: null,
            lastChargeExplorerLink: null,
            lastTxTimestamp: Date.now(),
          })
          navigate('/charge/success')
        }
      } catch {
        // ignore transient errors
      }
    }
    poll()
    const interval = window.setInterval(poll, 3000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [phase, chargeId, navigate, t])

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('chargeReturn.title')} onBack={goBack} />

      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        {phase === 'checking' && (
          <>
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-yokoso-red border-t-transparent" />
            <p className="mt-4 font-body text-sm text-text-secondary">
              {t('chargeReturn.checking')}
            </p>
          </>
        )}

        {phase === 'polling' && (
          <>
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-matcha border-t-transparent" />
            <p className="mt-4 font-body text-sm text-text-primary">
              {t('chargeReturn.polling')}
            </p>
            <p className="mt-1 font-body text-xs text-text-secondary">
              {t('chargeReturn.pollingSub')}
            </p>
            {/* #1189 Phase 3: demo mode 限定シミュレートボタン。
               Stripe CLI + `stripe listen` を叩ける人しか webhook を発火できず
               polling が永遠に終わらない属人化を解消する。ChargeBank の
               simulate ボタンと同じ debug endpoint を叩く (mode 問わず動く)。 */}
            {demoUiEnabled() && chargeId && (
              <div className="mt-8 w-full max-w-xs rounded-lg border border-dashed border-navy/40 bg-navy/5 p-3">
                <p className="font-body text-[11px] font-semibold uppercase tracking-wide text-navy">
                  {t('chargeReturn.demoBoxTitle')}
                </p>
                <p className="mt-1 font-body text-xs text-text-secondary leading-5 text-left">
                  {t('chargeReturn.demoBoxBody')}
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.simulateBankTransferComplete(chargeId)
                      // 既存 polling が数秒以内に executed 検知して自動遷移
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e))
                    }
                  }}
                  className="tap-effect mt-2 h-10 w-full rounded-lg bg-navy font-display text-xs font-semibold text-white"
                >
                  {t('chargeReturn.demoSimulateCta')}
                </button>
              </div>
            )}
          </>
        )}

        {/* #2190 項目 5 結果が分からない。払い直しは誘わず、ウォレットで残高を確かめてもらう */}
        {phase === 'unconfirmed' && (
          <>
            <p className="font-body text-sm font-semibold text-text-primary">
              {t('chargeReturn.unconfirmedTitle')}
            </p>
            <p className="mt-2 font-body text-xs text-text-secondary leading-relaxed">
              {t('chargeReturn.unconfirmedBody')}
            </p>
            <button
              onClick={() => navigate('/wallet')}
              className="tap-effect mt-6 h-12 w-full max-w-xs rounded-xl bg-navy font-display text-base font-semibold text-white"
            >
              {t('chargeReturn.toWallet')}
            </button>
          </>
        )}

        {phase === 'failed' && (
          <>
            <div className="rounded-full bg-red-100 p-4">
              <div className="h-8 w-8 text-red-600">✕</div>
            </div>
            <p className="mt-4 font-body text-sm font-semibold text-red-600">
              {t('chargeReturn.failedTitle')}
            </p>
            {error && (
              <p className="mt-2 font-body text-xs text-red-500 break-all">{error}</p>
            )}
            <button
              onClick={() => navigate('/charge')}
              className="tap-effect mt-6 h-12 w-full max-w-xs rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button"
            >
              {t('chargeReturn.retryCta')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
